import React, { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CalculateSvg from "../../assets/img/svg/calculate.svg";
import SweetAlertService from "../../utils/sweetalert";
import { useLocation } from "react-router-dom";

interface InstallmentDetail {
    month: number;
    principal: number;
    interest: number;
    totalPayment: number;
    remainingLoan: number;
}

export default function SimulationCalculate() {
    const [loanType, setLoanType] = useState("");
    const [loanAmount, setLoanAmount] = useState("");
    const [duration, setDuration] = useState("");
    const [interestRate, setInterestRate] = useState("");
    const [installmentDetails, setInstallmentDetails] = useState<InstallmentDetail[]>([]);
    const [isDeposito, setIsDeposito] = useState(false);
    const [isChecked, setIsChecked] = useState(false); // State untuk checkbox
    const location = useLocation();

    const calculateInstallmentDetails = (
        loanAmount: number,
        duration: number,
        interestRate: number,
        type: string
    ): InstallmentDetail[] => {
        const monthlyPrincipal = loanAmount / duration; //angsuran pokok perbulan sebelum bunga
        const details: InstallmentDetail[] = [];

        for (let month = 1; month <= duration; month++) {
            let interest;

            if (type === "flat") {
                interest = (loanAmount * interestRate) / 100;
            } else {
                const remainingLoan = loanAmount - monthlyPrincipal * (month - 1);
                interest = (remainingLoan * interestRate) / 100 / 12;
            }

            const remainingLoan = loanAmount - monthlyPrincipal * month;

            details.push({
                month,
                principal: monthlyPrincipal,
                interest,
                totalPayment: monthlyPrincipal + interest,
                remainingLoan: remainingLoan > 0 ? remainingLoan : 0,
            });
        }

        return details;
    };

    const handleCalculate = (e: React.FormEvent) => {
        e.preventDefault();

        if (!loanType || !loanAmount || !duration || !interestRate) {
            SweetAlertService.fieldCantNull();
            return;
        } else if (
            parseFloat(loanAmount) <= 0 ||
            parseFloat(duration) <= 0 ||
            parseFloat(interestRate) <= 0
        ) {
            SweetAlertService.fieldCantMinus();
            return;
        }

        const loanAmountNum = parseFloat(loanAmount);
        const durationNum = parseFloat(duration);
        const interestRateNum = parseFloat(interestRate);

        const details = calculateInstallmentDetails(
            loanAmountNum,
            durationNum,
            interestRateNum,
            loanType
        );

        setInstallmentDetails(details);
    };

    const handleIsDeposito = () => setIsDeposito(true);
    const handleIsCredit = () => setIsDeposito(false);

    const handleReset = () => {
        setLoanType("");
        setLoanAmount("");
        setDuration("");
        setInterestRate("");
        setInstallmentDetails([]);
        setIsChecked(false);
    };

    const handleCheckboxChange = () => {
        setIsChecked(!isChecked);
    };

    const formartCurrency = (value: number) => {
        return new Intl.NumberFormat('id-ID', {style: 'currency', currency: 'idr'}).format(value);
    }


    useEffect(() => {
        if (location.hash) {
            const element = document.querySelector(location.hash);
            if (element) {
                element.scrollIntoView();
            }
        }
    }, [location]);

    return (
        <div className="max-w-screen-2xl mx-auto pt-16 pb-32 px-5 md:px-5 lg:px-16" id="simulation">
            <h1 className="text-2xl font-poppinsBold text-bprDarkBlue text-center mb-10">
                SIMULASI
            </h1>
            <div className="border p-10 rounded-xl shadow-xl">
                {installmentDetails.length === 0 ? (
                    <div className="flex flex-col gap-6 lg:flex-row">
                        <div className="flex flex-col items-center">
                            <LazyLoadImage
                                src={CalculateSvg}
                                alt="Calculate"
                                effect="blur"
                                width={300}
                                height={300}
                                wrapperProps={{
                                    style: { transitionDelay: "1s" },
                                }}
                            />
                            <div className="w-full max-w-lg mt-8 md:mt-16 lg:mt-32 text-textPrimary px-4 md:px-0">
                                <span className="font-semibold">Syarat & Ketentuan</span>
                                <ul className="ml-4 md:ml-7 list-disc">
                                    <li>
                                        Perincian Kredit di atas hanya merupakan simulasi/ estimasi
                                        biaya.
                                    </li>
                                    <li>
                                        Perhitungan tersebut masih belum termasuk biaya lain-lain.
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="flex-1">
                            <div className="mb-8 space-x-5">
                                <button
                                    onClick={handleIsCredit}
                                    className={`${isDeposito ? "" : "border-b-4 border-bprDarkBlue"
                                        } text-lg font-semibold pb-2 hover:text-blue-900`}
                                >
                                    SIMULASI KREDIT
                                </button>
                            </div>
                            {isDeposito ? (
                                <h2>deposito</h2>
                            ) : (
                                <form onSubmit={handleCalculate} className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium block">
                                            Tipe Pinjaman
                                        </label>
                                        <select
                                            value={loanType}
                                            onChange={(e) => setLoanType(e.target.value)}
                                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                        >
                                            <option value="">Pilih Tipe...</option>
                                            <option value="flat">Flat</option>
                                            <option value="efektif">Efektif</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium block">
                                            Jumlah Pinjaman
                                        </label>
                                        <input
                                            type="number"
                                            value={loanAmount}
                                            onChange={(e) => setLoanAmount(e.target.value)}
                                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                            placeholder="Masukkan jumlah pinjaman"
                                        />
                                        <span>
                                            {formartCurrency(parseFloat(loanAmount) || 0)}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium block">
                                                Jangka Waktu
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="number"
                                                    value={duration}
                                                    onChange={(e) => setDuration(e.target.value)}
                                                    className="w-full md:w-24 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                                    placeholder="5"
                                                />
                                                <span className="self-center text-sm">Bulan</span>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium block">
                                                Suku Bunga
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={interestRate}
                                                    onChange={(e) => setInterestRate(e.target.value)}
                                                    className="w-full md:w-24 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                                    placeholder="1.75"
                                                />
                                                <span className="self-center text-sm">%</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="agreement"
                                            checked={isChecked}
                                            onChange={handleCheckboxChange}
                                            className="mr-2"
                                        />
                                        <label htmlFor="agreement" className="text-sm">
                                            Saya memahami bahwa hasil simulasi ini merupakan estimasi, hasil akhir
                                            sebenarnya dapat berbeda.
                                        </label>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={!isChecked}
                                        className="w-full bg-bprDarkBlue text-white py-2 rounded-md hover:bg-blue-900 transition-colors"
                                    >
                                        Hitung
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6 lg:flex-row">
                        <div className="flex flex-col items-center">
                            <LazyLoadImage
                                src={CalculateSvg}
                                alt="Calculate"
                                effect="blur"
                                width={300}
                                height={300}
                                wrapperProps={{
                                    style: { transitionDelay: "1s" },
                                }}
                            />
                            <div className="w-full max-w-lg mt-8 md:mt-16 lg:mt-32 text-textPrimary px-4 md:px-0">
                                <span className="font-semibold">Syarat & Ketentuan</span>
                                <ul className="ml-4 md:ml-7 list-disc">
                                    <li>
                                        Perincian Kredit di atas hanya merupakan simulasi/ estimasi
                                        biaya.
                                    </li>
                                    <li>
                                        Perhitungan tersebut masih belum termasuk biaya lain-lain.
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="flex-1">
                            <div className="border p-6 rounded-lg">
                                <p className="text-lg font-semibold text-center mb-6">
                                    PERKIRAAN RINCIAN ANGSURAN BULANAN
                                </p>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full border-collapse">
                                        <thead>
                                            <tr className="bg-bprDarkBlue text-white">
                                                <th className="border px-4 py-2">Bulan</th>
                                                <th className="border px-4 py-2">Angsuran Pokok</th>
                                                <th className="border px-4 py-2">Bunga</th>
                                                <th className="border px-4 py-2">Total Pembayaran</th>
                                                <th className="border px-4 py-2">Sisa Pinjaman</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {installmentDetails.map((detail) => (
                                                <tr key={detail.month} className="hover:bg-gray-50">
                                                    <td className="border px-4 py-2 text-center">{detail.month}</td>
                                                    <td className="border px-4 py-2 text-right">
                                                        Rp {detail.principal.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="border px-4 py-2 text-right">
                                                        Rp {detail.interest.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="border px-4 py-2 text-right">
                                                        Rp {detail.totalPayment.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className='border px-4 py-2 text-right'>
                                                        Rp {detail.remainingLoan.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <button
                                onClick={handleReset}
                                className="mt-4 w-full bg-bprDarkBlue text-white py-2 rounded-md hover:bg-blue-500 transition-colors"
                            >
                                Hitung Kembali
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
